import { Component, computed, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { Subscription } from 'rxjs'

import { UserService } from './shared/user/user.service';
import { UiModalService } from '@ui/ui-modal.service';
import { Season } from '@aifs-shared/application/season';
import { CommonModule } from '@angular/common';
import { SystemAlertComponent } from './user/system-alert.component';
import { SeasonClosedComponent } from './season-closed.component';
import { ApplicantStatusComponent } from '@aifs-shared/application/applicant-status.component';
import { InterviewService } from '@aifs-shared/interview/interview.service';
import { MessageService } from './messages/message.service';
import { InterviewInfoComponent } from '@aifs-shared/interview/interview-info.component';
import { ApplicationInterviewDetails } from '@aifs-shared/interview/application-interview-details';
import { TrackerService } from '@aifs-shared/tracker/tracker.service';
import { PlacementService } from './placement/placement.service';
import { ApplicationService } from '@aifs-shared/application/application.service';
import { EventService } from './event/event.service';
import { ToDoComponent } from '@aifs-shared/todo/todo.component';
import { ToDoItem } from '@aifs-shared/todo/todo-item';
import { AllEvents } from './event/all-events';
import { DateTime } from 'luxon';
import { PlacementDates } from './placement/placement-dates';
import { CfButtonComponent, CFButtonStyle, CFButtonType } from '@ui/cf-button/cf-button.component';
import { CfAlertComponent, CFAlertType } from '@ui/cf-alert/cf-alert.component';
import { CfLoaderComponent } from '@ui/cf-loader/cf-loader.component';


@Component({
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    imports: [CommonModule, SystemAlertComponent, SeasonClosedComponent, ApplicantStatusComponent, InterviewInfoComponent, ToDoComponent, CfButtonComponent, CfAlertComponent, CfLoaderComponent]
})
export class HomeComponent {
    private router = inject(Router);
    private userService = inject(UserService);
    private modal = inject(UiModalService);
    private applicationService = inject(ApplicationService);
    private interviewService = inject(InterviewService);
    private messageService = inject(MessageService);
    private eventService = inject(EventService);
    private placementService = inject(PlacementService);
    private tracker = inject(TrackerService);
    private modalService = inject(UiModalService);

    ButtonType = CFButtonType;
    ButtonStyle = CFButtonStyle;
    AlertType = CFAlertType;

    // CA-3561 check for blocked nationality
    isNationalityPermitted = computed(() => this.userService.isNationalityPermitted());
    needsUrgentContact = computed(() => {
        return this.userService.mustContactCAUrgently() || !this.isNationalityPermitted();
    });

    constructor() {
    }

    ngOnInit() {
        if (!this.userService.isApplicant()) {
            // console.debug(`Is not an applicant, redirect!`);
            if (this.userService.isInterviewer()) {
                this.modal
                    .showModalInformation('Interviewer Information',
                        '<p>Did you mean to sign in as an interviewer to the Abeona Portal?</p><p>Click <a href="https://portal.mycampamerica.com">here</a>, if so!</p>'
                    )
                    .subscribe({
                        next: (result: string) => {

                        }
                    })
                return;
            }

            // Well, they're neither fish nor fowl, so
            // let's clear out what is in storage and
            // require a sign-in
            this.userService.logout();
            this.router.navigateByUrl("/sign-in");

            // Most important - exit
            return;
        }

        // We're valid
        this.userService.reloadUserData();

        this.tracker
            .loadTasksForApplicant()
            .subscribe(
                result => {
                    this.initialise();
                });

    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    initialise(): void {
        if (this.userService.isApplicant()) {
            this.taskDataLoaded = true;
            // console.log(`CHECK FOR TERMS`);
            if (!this.userService.hasLatestTermsAndConditions()) {
                console.warn(`USER NEEDS NEW TERMS AND CONDITIONS`);
                this.tracker.homeRoute = "/home";
                this.router.navigateByUrl("/user/terms-and-conditions/latest/ANY");
                return;
            }

            if (this.tracker.overrideTask) {
                this.router.navigateByUrl(this.tracker.overrideTask);
                // Don't bother continuing at this point
                return;
            } else {
                console.log(`No override task found`);
            }

            const role = this.userService.applicantRole();
            if (role) {
                var activeApplicationId = this.userService.activeApplicationId();
                var applicantId = this.userService.applicantId();
                if (this.isNationalityPermitted()) {
                    this.checkApplyForNewSeason(activeApplicationId);
                    this.getNewMessages();
                    if (activeApplicationId > 0) {
                        this.getReturnerReadyToSubmit(applicantId);
                        this.getInterviewerDetails(activeApplicationId);
                        this.getAvailability();
                    }
                }
            }
        }
        //         });
    }

    checkApplyForNewSeason(applicationId: number) {
        this.applicationService
            .checkForNewSeason(applicationId)
            .subscribe(
                {
                    next: (season: Season) => {
                        // console.log(`Got season: ${JSON.stringify(season)}`);
                        if (season && season.isCurrent) {
                            this.season = season;
                            this.season.seasonNumber = season.seasonNumber;
                            this.showNewSeason = true;
                            this.showToDoList = false;
                            this.seasonOpenMessage = `<h5>${season?.seasonNumber} Applications Open</h5>`;
                            // console.log(this.seasonOpenMessage);

                            if (season.userState && season.userState == "ParticipantPlaced") {
                                this.applicantAtCamp = true;
                            }

                            // CA-3462
                            if (applicationId === 0 && season.nextSeasonOpen) {
                                this.showNextSeasonMessage = true;
                            }
                        }
                        else {
                            var countryCode = this.userService.userCountry();
                            this.getEvents(applicationId, countryCode);
                            this.showToDoList = true;
                        }
                    },
                    error: (error: any) => this.pageError(<any>error)
                }
            );
    }

    getEvents(applicationId: number, countryCode: string) {
        this.eventService
            .getAllEvents(applicationId, countryCode)
            .subscribe({
                next: (eventDetails: AllEvents) => {
                    if (eventDetails.allAttendingEvents.length > 0) {
                        switch (eventDetails.allAttendingEvents[0].eventType) {
                            case 400://Orientation
                                this.eventButtonText = "Upcoming Orientations";
                                this.eventClass = 'orientation';
                                break;
                            default:
                                this.eventButtonText = "Get Hired On The Spot!";
                                this.eventClass = 'fair';
                                this.registerButtonText = "Upcoming camp job fairs";
                                this.showSignUpForCDFair = (!(this.userService.isPlaced() || this.userService.isOutOfProgramme()));
                                break;
                        }
                        this.showEventButton = true;
                    }
                    if (eventDetails.allUnattendingEvents.length > 0) {
                        switch (eventDetails.allUnattendingEvents[0].eventType) {
                            case 200://Recruitment Fairs
                                this.eventButtonText = "Get Hired On The Spot!";
                                this.eventClass = 'fair';
                                this.showSignUpForCDFair = (!(this.userService.isPlaced() || this.userService.isOutOfProgramme()));
                                break;
                            default:
                                this.eventButtonText = "Upcoming Orientations";
                                this.eventClass = 'orientation';
                                break;
                        }
                        this.showEventButton = true;
                    }
                    const role = this.userService.applicantRole()
                    if (role) {
                        var userState = role.state!;
                        var season = role.activeApplicationSeason!; //applicants from 2020 of status UNS or PFT should be shown a rollover offer instead
                        if (userState == 'RejectedByOffice'
                            || userState == 'RejectedByInterviewer'
                            || userState == 'ParticipantOutOfProgrammeCNX'
                            || userState == 'ParticipantOutOfProgrammeEXP'
                            || userState == 'ParticipantOutOfProgrammeFAI'
                            || userState == 'ParticipantOutOfProgrammeREJ'
                            || (userState == 'ParticipantOutOfProgrammeUNS' && season != 2020)
                            || userState == 'ParticipantOutOfProgrammeTER') {
                            this.showEventButton = false;
                            this.showToDoList = false;
                        }
                    }
                },
                error: (error: any) => this.pageError(<any>error)
            });
    }

    getReturnerReadyToSubmit(applicantId: number) {
        this.tracker
            .getReturnerSubmittedApplication(applicantId)
            .subscribe({
                next: (readyToSubmit: boolean) => {
                    this.showSubmitApplicationButton = readyToSubmit;
                },
                error: (error: any) => this.pageError(<any>error)
            });
    }

    getAvailability() {
        const role = this.userService.applicantRole()
        if (role) {
            var currentState = role.state!;
            if (currentState.includes("ParticipantApplied") || currentState.includes("ParticipantReadyToPlace")) {
                this.applicationService
                    .loadFromUrl("availability")
                    .subscribe({
                        next: (result: any) => {
                            const e0 = DateTime.fromISO(result.earliestAvailabilityDate);
                            const e1 = DateTime.fromISO(result.dateDueHome);
                            if (e0 && e1) {
                                this.e0ord = this.getOrdinal(e0.day);
                                this.e1ord = this.getOrdinal(e1.day);
                            }

                            this.earliestAvailabilityDate = result.earliestAvailabilityDate
                            this.dateDueHome = result.dateDueHome
                            this.showEditAvailability = true;
                        },
                        error: (error: any) => {
                            this.pageError(error);
                        }
                    });
            } else if (currentState === "ParticipantPlaced") {
                this.placementService
                    .getPlacementDates(role.activeApplicationId!)
                    .subscribe({
                        next: (result: PlacementDates) => {
                            if (!result.isOT) {
                                if (result.startDate) {
                                    this.showPlacementDates = true;
                                    this.startDate = result.startDate;
                                    this.endDate = result.endDate!;
                                }
                            }
                        },
                        error: (error: any) => {
                            this.pageError(error);
                        }
                    });
            }
        }
    }

    getOrdinal(day: number): string {
        switch (day) {
            case 1:
            case 21:
            case 31:
                return 'st';
            case 2:
            case 22:
                return 'nd';
            case 3:
            case 23:
                return 'rd';
            default:
                return 'th';
        }
        return '';
    }

    getNewMessages() {
        this.messageService
            .getUnreadMessageCount()
            .subscribe({
                next: (result: number) => {
                    this.unreadCount = result;
                    let pl = result > 1 ? 's' : '';
                    this.unreadCountMessage = `${result} new message${pl}`;
                },
                error: (error: any) => {
                    this.pageError(error);
                }
            });
    }

    readMessages() {
        this.router.navigateByUrl("/messages");
    }

    getInterviewerDetails(applicationId: number) {
        if (applicationId === 0) return;

        this.interviewService
            .getApplicationInterviewDetails(applicationId)
            .subscribe({
                next: (applicationInterviewDetails: ApplicationInterviewDetails) => {
                    this.interviewInfo = applicationInterviewDetails;
                    const interview = this.interviewInfo?.interview;
                    if (interview) {
                        if ((interview.isOnline && (interview.status == 'PEND' || interview.status == 'REQ'))
                            || (!interview.isOnline && interview.status == 'PEND')) {
                            this.displayInterviewInfo = true;
                        }
                    }

                },
                error: (error: any) => this.pageError(<any>error)
            });
    }

    showInfoDialog() {
        this.modalService
            .showModalConfirmation(
                'Change Availability',
                '<p>By extending your availability date range you could increase your placement chances by being eligible for more camps! Would you like to edit your availability date?</p>',
                [
                    { id: 'Y', text: 'Yes', type: CFButtonType.Default },
                    { id: 'N', text: 'No', type: CFButtonType.Cancel },
                ]
            ).subscribe({
                next: (r: string) => {
                    if (r === 'Y') {
                        this.tracker.setReturnUrl("/home");
                        this.tracker.setActiveTask(this.tracker.tasksForGroup(8)[0]);
                        this.router.navigateByUrl("/form/availability");
                    }
                }
            });
    }

    showPlacementInfoDialog(ev: Event) {
        ev.preventDefault();
        this.modalService
            .showModalInformation(
                'Change Availability',
                '<p>If your availability changes and you are no longer able to complete your placement on these dates, please <a href="/contact-us/">contact us</a> immediately.</p>'
            );
    }

    upcomingEvents() {
        this.router.navigateByUrl("/events");
    }

    editItem(item: ToDoItem) {
        // CA-2511 some of these items are also tasks
        if (item.associatedTask) {
            this.tracker.setActiveTask(item.associatedTask);
        }
        this.router.navigateByUrl(item.url);
    }

    apply() {
        this.tracker.setActiveTask(this.tracker.tasksForGroup(1)[0]);
        this.router.navigateByUrl("/user/create-application");
    }

    submitApplication() {
        this.router.navigateByUrl("/payments/returner");
    }

    pageError(errorMessage: any): void {
        console.warn(`ERROR on page! - '${errorMessage}' - Disabling NEXT button`);
        const lerr = sessionStorage.getItem('lastError');
    }

    protected subscription?: Subscription;

    earliestAvailabilityDate?: Date;
    dateDueHome?: Date;
    e0ord = '';
    e1ord = '';

    unreadCount = 0;
    unreadCountMessage = 'No unread messages';
    showNewSeason = false;
    season?: Season;
    showSignUpForCDFair = false;
    eventButtonText?: string;
    eventClass?: string;
    showSubmitApplicationButton = false;
    showToDoList = false;
    displayInterviewInfo = false;
    interviewInfo!: ApplicationInterviewDetails;
    showEditAvailability = false;
    showPlacementDates = false;
    startDate = '';
    endDate = '';
    showEventButton = false;
    registerButtonText = 'Register for upcoming camp job fairs';
    applicantAtCamp = false;
    showNextSeasonMessage = false;
    seasonOpenMessage = '';
    taskDataLoaded = false;
}
